import type { BookHeader, CrewEntry, LogEntry } from "./boardbook-mapping";
import {
  APPROACH_TYPES,
  CREW_ROLES,
  FLIGHT_PURPOSES,
  FUEL_UNITS,
  HEMS_MISSION_SUBTYPES,
  HEMS_MISSION_TYPES,
  contentId,
  emptyCrew,
  emptyEntry,
} from "./boardbook-mapping";

/**
 * Wire identity + body codec for the boardbook kernel dataset.
 *
 * The kernel stores an **opaque body** keyed by (tenant, dataset, scope, uuid).
 * For boardbook: dataset = `boardbook`, scope = the aircraft registration, and
 * **uuid = the entry id** for a leg, or the fixed `HEADER_UUID` for the book
 * header row — i.e. ONE kernel row per log entry plus one header row (per-entry
 * sync), not one blob per book. Each row LWW-resolves independently, so a leg
 * added on one device and a leg added on another are two distinct rows that
 * both survive.
 *
 * The body is a plain JSON object. `bodyToEntry`/`bodyToHeader` are the
 * **trust boundary**: a remote body is untrusted, so it is defensively coerced.
 * The envelope carries the authoritative `id`/`uuid` and `updatedAt` (kernel
 * LWW column); every body field falls back to its empty default.
 */
export const BOARDBOOK_DATASET = "boardbook";

/** The uuid of the kernel row holding the book header (`BookHeader`). */
export const HEADER_UUID = "header";

/**
 * The uuid of the pre-per-entry **whole-book** row (dataset `boardbook`, a single
 * fixed uuid holding `{entries:[...]}`). A device upgraded from that format still
 * receives this row on subscribe; the service explodes it into per-entry rows and
 * tombstones it (see `legacyBlobToEntries`).
 */
export const LEGACY_BOOK_UUID = "boardbook";

/** Serialize one entry to its opaque body. `id` rides the uuid and `updatedAt`
 * the kernel column, but keep both in the body too so a body is self-describing. */
export function entryToBody(entry: LogEntry): Record<string, unknown> {
  return { ...entry };
}

/** Serialize the book header to its opaque body. */
export function headerToBody(header: BookHeader): Record<string, unknown> {
  return { ...header };
}

/**
 * Coerce a remote per-entry body into a `LogEntry`. `id`/`updatedAt` come from the
 * envelope (the kernel row's uuid + LWW column) — authoritative over the body.
 * Returns null when the body isn't an object (unusable row).
 */
export function bodyToEntry(uuid: string, updatedAt: number, body: unknown): LogEntry | null {
  if (!isRecord(body)) return null;
  return coerceEntry(body, uuid, updatedAt);
}

/**
 * Coerce a remote book-header body into a `BookHeader`. `updatedAt` comes from the
 * envelope (the kernel row's LWW column) — authoritative over the body.
 * Returns null when the body isn't an object (unusable row).
 */
export function bodyToHeader(updatedAt: number, body: unknown): BookHeader | null {
  if (!isRecord(body)) return null;
  return {
    updatedAt,
    dateOpened: dateOf(body["dateOpened"]),
    dateClosed: dateOf(body["dateClosed"]),
    fuelUnit: enumOf(body["fuelUnit"], FUEL_UNITS),
    cfFlightTime: num(body["cfFlightTime"]),
    cfBlockTime: num(body["cfBlockTime"]),
    cfLandings: num(body["cfLandings"]),
    cfEngine1N1: num(body["cfEngine1N1"]),
    cfEngine1N2: num(body["cfEngine1N2"]),
    cfEngine1Cdv: num(body["cfEngine1Cdv"]),
    cfEngine2N1: num(body["cfEngine2N1"]),
    cfEngine2N2: num(body["cfEngine2N2"]),
    cfEngine2Cdv: num(body["cfEngine2Cdv"]),
    cfWinchHours: num(body["cfWinchHours"]),
  };
}

/**
 * Explode a retained legacy whole-book body into per-entry rows. The old format
 * was either `{ entries: [...] }` (multi-entry blob) or a **flat** single leg
 * (`{date,captain,...}`, no `entries`). Each entry gets its id from its own
 * stored `id` or a deterministic `contentId`, so two devices exploding the same
 * blob derive the SAME uuids and dedup instead of duplicating. Per-entry
 * `updatedAt` comes from the entry's own stamp, falling back to the book stamp.
 */
export function legacyBlobToEntries(body: unknown): LogEntry[] {
  if (!isRecord(body)) return [];
  const b = body;
  const bookStamp = Number(b["updatedAt"]);
  const bookUpdatedAt = Number.isFinite(bookStamp) ? bookStamp : 0;

  const raw = b["entries"];
  if (Array.isArray(raw)) {
    return raw.map((e) => coerceLegacyEntry(e, bookUpdatedAt));
  }
  // Flat single-leg body: reconstruct one entry from the leg fields. A genuinely
  // empty/cleared body (no leg fields) yields nothing.
  const legFields = ["date", "captain", "departure", "destination", "blockOff", "blockOn", "remarks"];
  const hasLeg = legFields.some((k) => typeof b[k] === "string" && b[k] !== "");
  return hasLeg ? [coerceLegacyEntry(b, bookUpdatedAt)] : [];
}

/** Coerce a legacy entry object whose id/updatedAt live in the body itself
 * (there is no per-row envelope for a legacy blob). */
function coerceLegacyEntry(raw: unknown, bookUpdatedAt: number): LogEntry {
  const e = isRecord(raw) ? raw : {};
  const entryStamp = Number(e["updatedAt"]);
  const updatedAt = Number.isFinite(entryStamp) && entryStamp > 0 ? entryStamp : bookUpdatedAt;
  const built = coerceEntry(e, str(e["id"]), updatedAt);
  return built.id ? built : { ...built, id: contentId(built) };
}

function coerceEntry(raw: unknown, id: string, updatedAt: number): LogEntry {
  if (!isRecord(raw)) return { ...emptyEntry(), id, updatedAt };
  const b = raw;
  return {
    id,
    updatedAt,
    lockedAt: isoOf(b["lockedAt"]),
    date: dateOf(b["date"]),
    departure: str(b["departure"]),
    departureName: str(b["departureName"]),
    destination: str(b["destination"]),
    destinationName: str(b["destinationName"]),
    flightNumber: str(b["flightNumber"]),
    flightRule: b["flightRule"] === "IFR" ? "IFR" : "VFR",
    flightPurpose: enumOf(b["flightPurpose"], FLIGHT_PURPOSES),
    approachType: enumOf(b["approachType"], APPROACH_TYPES),
    blockOff: hhmmOf(b["blockOff"]),
    takeoff: hhmmOf(b["takeoff"]),
    landing: hhmmOf(b["landing"]),
    blockOn: hhmmOf(b["blockOn"]),
    landings: landingsOf(b["landings"]),
    fuelUplBefore: num(b["fuelUplBefore"]),
    fuelAtTakeoff: num(b["fuelAtTakeoff"]),
    fuelUplAfter: num(b["fuelUplAfter"]),
    fuelAtLanding: num(b["fuelAtLanding"]),
    passengers: num(b["passengers"]),
    missionType: enumOf(b["missionType"], HEMS_MISSION_TYPES),
    missionSubtype: enumOf(b["missionSubtype"], HEMS_MISSION_SUBTYPES),
    catA: b["catA"] === true,
    engine1N1: num(b["engine1N1"]),
    engine1N2: num(b["engine1N2"]),
    engine1Cdv: num(b["engine1Cdv"]),
    engine2N1: num(b["engine2N1"]),
    engine2N2: num(b["engine2N2"]),
    engine2Cdv: num(b["engine2Cdv"]),
    winchCycles: num(b["winchCycles"]),
    winchMotorHours: num(b["winchMotorHours"]),
    deliveryNote: str(b["deliveryNote"]),
    internalRef: str(b["internalRef"]),
    remarks: str(b["remarks"]),
    crew: crewOf(b),
  };
}

/** `body.crew` when it is an array (each element defensively coerced, invalid
 * ones dropped); else the LEGACY single-captain reconstruction. */
function crewOf(body: Record<string, unknown>): CrewEntry[] {
  const raw = body["crew"];
  if (Array.isArray(raw)) {
    const out: CrewEntry[] = [];
    for (const el of raw) {
      const c = coerceCrew(el);
      if (c) out.push(c);
    }
    return out;
  }
  const captain = body["captain"];
  return typeof captain === "string" && captain !== "" ? [{ ...emptyCrew("PIC"), familyName: captain }] : [];
}

/** Old stored role strings the `logbook-migrations` 2.0.0 (v3) migration renames on the local
 * DB. A kernel sync body written before that migration can still carry these; rename them the
 * same way before the `CREW_ROLES` check. */
const LEGACY_ROLE_RENAMES: Readonly<Record<string, CrewEntry["role"]>> = {
  COPILOT: "SIC",
  "TC-HEMS": "TCHEMS",
  INSTRUCTOR: "FI",
  EXAMINER: "FE",
};

/** Coerce one crew element; returns null when it isn't an object or its role
 * isn't a known `CrewRole` (the row is dropped by the caller). */
function coerceCrew(raw: unknown): CrewEntry | null {
  if (!isRecord(raw)) return null;
  const c = raw;
  const rawRole = c["role"];
  const role = typeof rawRole === "string" ? (LEGACY_ROLE_RENAMES[rawRole] ?? rawRole) : rawRole;
  if (typeof role !== "string" || !(CREW_ROLES as readonly string[]).includes(role)) return null;
  return {
    role: role as CrewEntry["role"],
    familyName: str(c["familyName"]),
    givenNames: str(c["givenNames"]),
    externalId: str(c["externalId"]),
    seat: str(c["seat"]),
    blockTime: num(c["blockTime"]),
    flightTime: num(c["flightTime"]),
    takeoffsDay: num(c["takeoffsDay"]),
    takeoffsNight: num(c["takeoffsNight"]),
    landingsDay: num(c["landingsDay"]),
    landingsNight: num(c["landingsNight"]),
    ifrApproaches: num(c["ifrApproaches"]),
    checkFlight: c["checkFlight"] === true,
    nightTime: num(c["nightTime"]),
    ifrTime: num(c["ifrTime"]),
    hoistDay: num(c["hoistDay"]),
    hoistNight: num(c["hoistNight"]),
    hoistStretcher: num(c["hoistStretcher"]),
    hoistDoublePax: num(c["hoistDoublePax"]),
    hoistSinglePax: num(c["hoistSinglePax"]),
    nvis: c["nvis"] === true,
    nvisCheckFlight: c["nvisCheckFlight"] === true,
  };
}

/** `v` when it is a string in `allowed`, else `""`. */
function enumOf<T extends string>(v: unknown, allowed: readonly T[]): T | "" {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : "";
}

/** `Math.floor` of a finite non-negative number (numeric strings allowed); else 0. */
function landingsOf(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

/** `true` (and narrows) when `v` is a non-null object — the shared "is this an
 * opaque-body-shaped value" test at every trust-boundary entry point. */
function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** `yyyy-mm-dd` when `v` is a real calendar date, else `""`. */
function dateOf(v: unknown): string {
  const s = str(v);
  const ms = /^\d{4}-\d{2}-\d{2}$/.test(s) ? Date.parse(`${s}T00:00:00Z`) : NaN;
  return Number.isFinite(ms) && new Date(ms).toISOString().startsWith(s) ? s : "";
}

/** `HH:mm` when `v` is a valid time of day, else `""`. */
function hhmmOf(v: unknown): string {
  const s = str(v);
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(s) ? s : "";
}

/** `v` as an ISO date-time when it parses, else `""` — `Leg.locked_at` must be a date-time. */
function isoOf(v: unknown): string {
  const ms = Date.parse(str(v));
  return Number.isFinite(ms) ? new Date(ms).toISOString() : "";
}

/** `null` for null/undefined/""/whitespace; a finite number (numeric strings
 * included); else `null`. */
function num(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
